import { Dimensions, FlatList, Image, View } from 'react-native'
import React, { useRef, useState } from 'react'
import { NavigationStackScreenComponent } from 'react-navigation-stack'
import {
  ATLButton,
  ATLTextBody,
  ATLTextHead,
  ButtonTypeEnum,
  TextAlignmentEnum,
} from '@appsimples/atlantica-react-native'
import styled from 'styled-components/native'
import { atlTheme } from '../../atlTheme'
import { OnboardingCards } from './OnboardingCards'
import { Coordinator } from '../../navigation/coordinator/coordinator'
import { Colors } from '../../constants/colors'
import { formatMessage } from '../../constants/strings/intl/intlHooks'
import { messages } from '../../constants/strings/messages'

const CarouselWrapper = styled.SafeAreaView`
  align-items: center;
  height: 100%;
  width: 100%;
  background-color: ${Colors.white};
`

const HollowIndicator = styled.View`
  margin: 0 ${atlTheme.SpacingAtomic}px;
  height: ${atlTheme.SpacingBase}px;
  width: ${atlTheme.SpacingBase}px;
  border-radius: ${atlTheme.SpacingAtomic}px;
  border-width: ${atlTheme.WidthThread}px;
  border-color: ${atlTheme.ColorBrand2Default};
`

const FilledIndicator = styled<any>(HollowIndicator)`
  background-color: ${atlTheme.ColorBrand2Default};
`

const IndicatorWrapper = styled.View`
  flex-direction: row;
  margin-top: -${atlTheme.SpacingBase}px;
`

const OnboardingImage = styled.Image`
  align-self: center;
  margin-bottom: ${atlTheme.SpacingBase}px;
`

const CardContainer = styled.View`
  flex-direction: column;
  justify-content: space-between;
  margin-top: ${atlTheme.SpacingIntersection}px;
  margin-bottom: ${atlTheme.SpacingDistribute}px;
  width: ${Dimensions.get('window').width}px;
`

const ImageAndTextContainer = styled.View`
  align-items: center;
`

const IconContainer = styled.View`
  align-items: center;
  position: absolute;
  bottom: ${atlTheme.SpacingWide}px;
  align-self: center;
`

export const OnboardingScreen: NavigationStackScreenComponent = () => {
  const [imageIndex, setImageIndex] = useState(0)
  const onViewRef = React.useRef((args: any) => {
    setImageIndex(args.changed[0].index)
  })
  const viewConfigRef = React.useRef({ viewAreaCoveragePercentThreshold: 100 })
  const flatListRef = useRef(null)

  const renderIndicator = () =>
    OnboardingCards &&
    OnboardingCards.map((element, index) => {
      if (index === imageIndex) return <FilledIndicator key={index} />
      else return <HollowIndicator key={index} />
    })

  const onClickGoToHome = () => {
    Coordinator.goToTransition()
  }

  const bottomButton = (index: number) => {
    return index === OnboardingCards.length - 1 ? (
      <ATLButton
        title={formatMessage(messages.onboardingScreen_goToHome_button)}
        onPress={onClickGoToHome}
        horizontalMargin={atlTheme.SpacingDistribute}
        buttonBaseProps={{ borderRadius: atlTheme.BorderRadius100 }}
      />
    ) : (
      <ATLButton
        title={formatMessage(messages.onboardingScreen_skip_button)}
        onPress={onClickGoToHome}
        type={ButtonTypeEnum.label}
        buttonBaseProps={{ textColorComplementary: atlTheme.ColorBrand2Default }}
      />
    )
  }

  const onClickContinue = () => {
    flatListRef.current.scrollToIndex({ animated: true, index: 1 })
  }

  const renderItem = (item, index) => {
    return (
      <CardContainer>
        <ImageAndTextContainer>
          <OnboardingImage source={item.image} />
          <ATLTextHead labelBaseProps={{ multilineLimit: 1, fontSize: atlTheme.FontSizeHighlight }}>
            {item.title}
          </ATLTextHead>
          <ATLTextBody textAlignment={TextAlignmentEnum.center} labelBaseProps={{ multilineLimit: 5 }}>
            {item.text}
          </ATLTextBody>
        </ImageAndTextContainer>
        <View>
          {index === 0 && (
            <ATLButton
              horizontalMargin={atlTheme.FontSizeDisplay}
              buttonBaseProps={{ borderRadius: atlTheme.BorderRadius100 }}
              title={formatMessage(messages.onboardingScreen_continue_button)}
              onPress={onClickContinue}
            />
          )}
          {item.icon && (
            <IconContainer>
              <Image source={item.icon} />
              <ATLTextBody
                labelBaseProps={{
                  multilineLimit: 1,
                  fontSize: atlTheme.FontSizeButton,
                  textColor: atlTheme.ColorBrand1Default,
                }}
                textAlignment={TextAlignmentEnum.center}
              >
                {item.name}
              </ATLTextBody>
            </IconContainer>
          )}
          {bottomButton(index)}
        </View>
      </CardContainer>
    )
  }

  return (
    <CarouselWrapper>
      <FlatList
        ref={flatListRef}
        keyExtractor={(item, index) => index.toString()}
        data={OnboardingCards || []}
        renderItem={({ item, index }) => renderItem(item, index)}
        horizontal
        showsHorizontalScrollIndicator={false}
        pagingEnabled
        onViewableItemsChanged={onViewRef.current}
        viewabilityConfig={viewConfigRef.current}
      />
      <IndicatorWrapper>{renderIndicator()}</IndicatorWrapper>
    </CarouselWrapper>
  )
}
OnboardingScreen.navigationOptions = {
  headerShown: false,
}
